import type { CSSProperties } from "react";
import type { Figura, Karta, Kolor } from "@gierki/shared";
import { figuraKarty, kolorKarty } from "@gierki/shared";

/**
 * Karta do gry rysowana **kodem**. Zero plików.
 *
 * Uzasadnienie jest arytmetyczne: 52 obrazki po 25–40 kB to 1,3–2 MB
 * doładowywane w chwili, gdy PWA startuje w aucie na LTE — czyli dokładnie
 * wtedy, gdy nie ma łącza. Cache Service Workera tego nie rozwiązuje, tylko
 * przesuwa: ktoś kiedyś musi je pobrać, a pierwszym „ktoś" jest zawsze osoba,
 * która właśnie chciała zagrać.
 *
 * Do tego bitmapa ma jedną rozdzielczość, a karta występuje na ekranie
 * w trzech (wachlarz ~34 px, wybrana ~72 px, wierzch stosu ~110 px) i we
 * wszystkich musi być ostra. Cztery ścieżki i trzynaście stringów skalują się
 * do dowolnego rozmiaru, działają offline od pierwszego uruchomienia
 * i nie mają żadnej licencji.
 */

/** Proporcja karty do gry — 2:3, jak w pudełku. */
const PROPORCJA = 1.5;

const CZERWONE: Kolor[] = ["kier", "karo"];

/** Barwa koloru karty to **reguła, nie ozdoba** — po niej rozpoznaje się kolor. */
function barwa(kolor: Kolor): string {
  return CZERWONE.includes(kolor) ? "var(--karta-czerwien)" : "var(--karta-czern)";
}

/** Nazwa do czytnika ekranu i do `title` — „król pik", nie „K-pik". */
const NAZWY_FIGUR: Record<string, string> = {
  J: "walet",
  Q: "dama",
  K: "król",
  A: "as",
};

export function nazwaKarty(karta: Karta): string {
  const figura = figuraKarty(karta);
  return `${NAZWY_FIGUR[figura] ?? figura} ${kolorKarty(karta)}`;
}

/**
 * Symbol koloru jako ścieżki w `viewBox="0 0 100 100"`.
 *
 * Trefl składa się z trzech kół i nóżki, bo jedna ścieżka o tym kształcie jest
 * nieczytelna przy 12 px — a właśnie tam, w rogu karty w wachlarzu, spędza
 * większość partii.
 */
export function SymbolKoloru({
  kolor,
  className = "",
  style,
}: {
  kolor: Kolor;
  className?: string;
  style?: CSSProperties;
}) {
  const fill = barwa(kolor);
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} fill={fill} aria-hidden="true">
      {kolor === "kier" ? (
        <path d="M50 88C50 88 10 60 10 36C10 22 21 12 34 12C42 12 48 16 50 21C52 16 58 12 66 12C79 12 90 22 90 36C90 60 50 88 50 88Z" />
      ) : kolor === "karo" ? (
        <path d="M50 6C60 26 74 40 92 50C74 60 60 74 50 94C40 74 26 60 8 50C26 40 40 26 50 6Z" />
      ) : kolor === "pik" ? (
        <>
          <path d="M50 10C50 10 90 40 90 62C90 74 80 83 68 83C60 83 53 79 50 73C47 79 40 83 32 83C20 83 10 74 10 62C10 40 50 10 50 10Z" />
          <path d="M50 58C50 76 46 87 38 94H62C54 87 50 76 50 58Z" />
        </>
      ) : (
        <>
          <circle cx="50" cy="28" r="18" />
          <circle cx="26" cy="60" r="18" />
          <circle cx="74" cy="60" r="18" />
          <path d="M45 58C45 76 42 87 34 94H66C58 87 55 76 55 58Z" />
        </>
      )}
    </svg>
  );
}

/**
 * Lico karty.
 *
 * Róg z figurą jest **przy lewej krawędzi**, bo w wachlarzu tylko lewy pasek
 * jest widoczny — i to on musi wystarczyć do rozpoznania karty.
 */
export function KartaDoGry({
  karta,
  szerokosc = 56,
  wybrana = false,
  przygaszona = false,
  onClick,
}: {
  karta: Karta;
  szerokosc?: number;
  wybrana?: boolean;
  przygaszona?: boolean;
  onClick?: () => void;
}) {
  const figura = figuraKarty(karta) as Figura;
  const kolor = kolorKarty(karta);
  const Element = onClick ? "button" : "div";

  return (
    <Element
      onClick={onClick}
      disabled={onClick && przygaszona ? true : undefined}
      title={nazwaKarty(karta)}
      aria-label={nazwaKarty(karta)}
      className={[
        "relative shrink-0 rounded-lg border overflow-hidden transition-transform",
        wybrana ? "-translate-y-3" : "",
        przygaszona ? "opacity-40" : "",
      ].join(" ")}
      style={{
        width: `${szerokosc}px`,
        height: `${Math.round(szerokosc * PROPORCJA)}px`,
        backgroundColor: "var(--karta-tlo)",
        borderColor: wybrana ? "var(--color-akcent)" : "var(--karta-obrys)",
        borderWidth: wybrana ? "2px" : "1px",
      }}
    >
      <span
        className="absolute left-1 top-0.5 flex flex-col items-center leading-none"
        style={{ color: barwa(kolor), fontSize: `${Math.max(11, Math.round(szerokosc * 0.3))}px` }}
      >
        <span className="font-semibold tabular">{figura}</span>
        <SymbolKoloru kolor={kolor} className="w-[0.7em] h-[0.7em] mt-0.5" />
      </span>

      {/* Duży symbol pośrodku ma sens dopiero, gdy karta jest szersza niż pasek
          w wachlarzu — przy 34 px zlewałby się z rogiem. */}
      {szerokosc >= 48 ? (
        <SymbolKoloru
          kolor={kolor}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ width: `${szerokosc * 0.45}px`, height: `${szerokosc * 0.45}px` }}
        />
      ) : null}
    </Element>
  );
}

/** Rewers: talia i cudze ręce. Jedna karta, dowolny rozmiar, zero plików. */
export function Rewers({ szerokosc = 56 }: { szerokosc?: number }) {
  return (
    <div
      className="shrink-0 rounded-lg border"
      style={{
        width: `${szerokosc}px`,
        height: `${Math.round(szerokosc * PROPORCJA)}px`,
        backgroundColor: "var(--karta-rewers)",
        borderColor: "var(--karta-obrys)",
        // Kratka z dwóch gradientów zamiast tekstury: wzór skaluje się razem
        // z kartą i nie kosztuje ani jednego żądania sieciowego.
        backgroundImage:
          "repeating-linear-gradient(45deg, var(--karta-rewers-wzor) 0 2px, transparent 2px 8px), repeating-linear-gradient(-45deg, var(--karta-rewers-wzor) 0 2px, transparent 2px 8px)",
      }}
      aria-hidden="true"
    />
  );
}
